import { useState, useEffect } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import { Grid, TextField } from '@mui/material';

type Props = {
    grid?: boolean;
    name: string;
    defVal: unknown;
    caption: string;
    size?: 'small' | 'medium';
    variant?: 'outlined' | 'filled' | 'standard';
    loader: () => Promise<unknown[]>;
    onChange: (event: { name: string | undefined; value: unknown }) => void;
    labelField?: string;
    valueField?: string;
    [key: string]: unknown;
};

/*
 * Generic <SELECT> with Autocomplete that loads arbitrary data using a provided 'loader' function
 * Due to AppContext.api() not returning Promises, the loader function that this component requires must return a promise (to preserve response context).
 *
 * <Promise> function loader(): loading function that must return a Promise that resolves with a list of rows, or rejects with the error message
 * String labelField: the key of the option's name
 * String valueField: the key of the option's id
 *
 * */
export function SttSelectAsync({
    grid,
    name,
    defVal,
    caption,
    size = 'small',
    variant = 'outlined',
    loader,
    onChange,
    labelField = 'name',
    valueField = 'id',
    ...rest
}: Props): React.ReactElement {
    const [items, setItems] = useState<Record<string, unknown>[]>([]);
    const [selected, setSelected] = useState<Record<string, unknown>>({
        [labelField]: '',
        [valueField]: defVal,
    });

    // Set the selected item (must search in the array after loading the data)
    useEffect(() => {
        if (items.length > 0 && defVal !== undefined) {
            const foundItem = items.find(
                (item: any) => item[valueField] === defVal,
            );
            if (foundItem) {
                setSelected(foundItem);
            }
        }
    }, [items]);

    function change(item: Record<string, unknown> | null) {
        setSelected(item || { [labelField]: '', [valueField]: undefined });

        if (onChange) {
            onChange({ name, value: item ? item[valueField] : undefined });
        }
    }

    // Run loader function and load items / throw error
    useEffect(() => {
        if (items.length === 0 && loader) {
            const promise = loader();
            if (promise && promise.constructor === Promise) {
                promise
                    .then((rows) => {
                        setItems(rows as Record<string, unknown>[]);
                    })
                    .catch((err: any) => {
                        console.log(err);
                    });
            } else {
                console.log(
                    'Warning: SttSelectAsyncData requires that the passed loader() function returns a Promise.',
                );
            }
        }
    }, []);

    // Render item
    const content = (
        <Autocomplete
            options={items}
            getOptionLabel={(option: any) =>
                (option[labelField] as string) || ''
            }
            onChange={(_ev: any, item: any) => change(item)}
            value={selected}
            renderInput={(params: any) => (
                <TextField
                    fullWidth
                    {...params}
                    size={size}
                    label={caption}
                    variant={variant}
                />
            )}
        />
    );

    return grid ? <Grid {...rest}>{content}</Grid> : content;
}
